<script lang="ts">
	import type { Component } from 'svelte';
	import type { LayoutData } from './$types';
	import { browser } from '$app/environment';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { page as pageStore } from '$app/state';
	import Cart from '$components/Cart/Cart.svelte';
	import { PUBLIC_SENTRY_ENVIRONMENT, PUBLIC_WEBPARKING_URL } from '$env/static/public';
	import { maybeSetAdvocateId } from '$lib/affiliate';
	import { setSiteContext } from '$lib/context/site';
	import { maybeUpdateFeatureFlagsFromUrl, updateFeatureFlags } from '$lib/featureFlags/client';
	import { inclusiveShopPresentation, setShopPresentation } from '$lib/shop/presentation.svelte';
	import { createCartState, setCartState } from '$lib/state/cart.svelte';
	import { createOrderDraftState, setOrderDraftState } from '$lib/state/order-draft.svelte';
	import { initStores } from '$lib/stores';
	import { onMount } from 'svelte';

	type BrowserWindow = Window & {
		requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
		cancelIdleCallback?: (handle: number) => void;
	};

	const GTM_DEFER_TIMEOUT_MS = 3000;
	const TOAST_LAYER_DEFER_TIMEOUT_MS = 1500;
	const deferredGtmEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
	const deferredUiEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;

	type LazyComponent = Component<any>;

	let { children, data } = $props();

	// Initialize stores.
	const stores = initStores();

	const orderDraft = createOrderDraftState();
	setOrderDraftState(orderDraft);

	// Set up new cart state.
	const cart = createCartState({
		allProducts: data.products,
		allFrequencies: data.frequencies,
		categoriesWithQuantities: data.categoriesWithQuantities
	});
	setCartState(cart);

	// Set up context.
	setSiteContext(data.site);
	setShopPresentation(inclusiveShopPresentation);

	// Init feature flags from server context
	if (data.flags?.flags) {
		updateFeatureFlags(data.flags.flags);
	}

	let pageType: string | undefined = $state(undefined);
	let loadedTrustedShopsId: string | null = null;
	let deferredGtmTimeoutId: number | null = null;
	let deferredGtmIdleCallbackId: number | null = null;
	let deferredToastTimeoutId: number | null = null;
	let deferredToastIdleCallbackId: number | null = null;
	let hasStartedCheckoutStore = false;
	let hasStartedUserStore = false;
	let modalComponentPromise: Promise<LazyComponent | null> | null = $state(null);
	let toastComponentPromise: Promise<LazyComponent | null> | null = $state(null);

	const hasPageLayout = !!pageStore.data?.page?.layout;
	const etrustedReviews = pageStore.data?.page?.layout?.reviews?.etrusted ?? null;
	const isProductionEnvironment = !PUBLIC_WEBPARKING_URL.includes('staging');

	const cmsCdnPreconnectHref = isProductionEnvironment
		? 'https://cdn.cms.thesubscriptioncompany.nl'
		: 'https://staging.cdn.cms.thesubscriptioncompany.nl';

	function shouldDelayGtmLoad() {
		return pageStore.url.pathname === '/';
	}

	function isHomepageRoute() {
		return pageStore.url.pathname === '/';
	}

	function shouldDeferSecondaryStores() {
		return isHomepageRoute();
	}

	function loadModalComponent() {
		if (!modalComponentPromise) {
			modalComponentPromise = import('$components/UI/Modals/Modal.svelte').then(
				({ default: component }) => component
			);
		}
	}

	function loadToastComponent() {
		clearDeferredToastMount();

		if (!toastComponentPromise) {
			toastComponentPromise = import('$components/UI/Toasts/Toasts.svelte').then(
				({ default: component }) => component
			);
		}
	}

	function mountSecondaryStores() {
		const { checkoutStore, userStore } = stores as {
			checkoutStore: { onMount?: () => void };
			userStore: { onMount?: () => void };
		};

		if (!hasStartedCheckoutStore && typeof checkoutStore.onMount === 'function') {
			checkoutStore.onMount();
			hasStartedCheckoutStore = true;
		}

		if (!hasStartedUserStore && typeof userStore.onMount === 'function') {
			userStore.onMount();
			hasStartedUserStore = true;
		}
	}

	function mountInitialStores() {
		if (!shouldDeferSecondaryStores()) {
			mountSecondaryStores();
		}
	}

	function clearDeferredGtmLoad() {
		if (!browser) {
			return;
		}

		const browserWindow = window as BrowserWindow;

		if (deferredGtmTimeoutId !== null) {
			window.clearTimeout(deferredGtmTimeoutId);
			deferredGtmTimeoutId = null;
		}

		if (deferredGtmIdleCallbackId !== null && browserWindow.cancelIdleCallback) {
			browserWindow.cancelIdleCallback(deferredGtmIdleCallbackId);
			deferredGtmIdleCallbackId = null;
		}

		deferredGtmEvents.forEach((eventName) => {
			window.removeEventListener(eventName, handleDeferredGtmInteraction);
		});
	}

	function clearDeferredToastMount() {
		if (!browser) {
			return;
		}

		const browserWindow = window as BrowserWindow;

		if (deferredToastTimeoutId !== null) {
			window.clearTimeout(deferredToastTimeoutId);
			deferredToastTimeoutId = null;
		}

		if (deferredToastIdleCallbackId !== null && browserWindow.cancelIdleCallback) {
			browserWindow.cancelIdleCallback(deferredToastIdleCallbackId);
			deferredToastIdleCallbackId = null;
		}

		deferredUiEvents.forEach((eventName) => {
			window.removeEventListener(eventName, handleDeferredToastInteraction);
		});
	}

	function handleDeferredToastInteraction() {
		loadToastComponent();
	}

	function scheduleToastLayerMount() {
		if (!browser) {
			return;
		}

		if (!isHomepageRoute()) {
			clearDeferredToastMount();
			loadToastComponent();
			return;
		}

		clearDeferredToastMount();

		deferredUiEvents.forEach((eventName) => {
			window.addEventListener(eventName, handleDeferredToastInteraction, {
				once: true,
				passive: true
			});
		});

		const browserWindow = window as BrowserWindow;

		if (browserWindow.requestIdleCallback) {
			deferredToastIdleCallbackId = browserWindow.requestIdleCallback(() => loadToastComponent(), {
				timeout: TOAST_LAYER_DEFER_TIMEOUT_MS
			});
		} else {
			deferredToastTimeoutId = window.setTimeout(
				() => loadToastComponent(),
				TOAST_LAYER_DEFER_TIMEOUT_MS
			);
		}
	}

	function handleDeferredGtmInteraction() {
		loadGtmScript(data.site.gtmCode);
	}

	function loadGtmScript(gtmCode?: string) {
		if (!gtmCode) {
			return;
		}

		clearDeferredGtmLoad();

		const windowWithDataLayer = window as Window & {
			dataLayer?: Array<Record<string, unknown>>;
		};

		const gtmSrc = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmCode)}`;

		if (document.querySelector(`script[src="${gtmSrc}"]`)) {
			return;
		}

		windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];
		windowWithDataLayer.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

		const gtmScript = document.createElement('script');
		gtmScript.src = gtmSrc;
		gtmScript.async = true;
		document.head.append(gtmScript);
	}

	function scheduleGtmLoad(gtmCode?: string) {
		if (!browser || !gtmCode) {
			return;
		}

		if (!shouldDelayGtmLoad()) {
			loadGtmScript(gtmCode);
			return;
		}

		clearDeferredGtmLoad();
		const windowWithDataLayer = window as Window & {
			dataLayer?: Array<Record<string, unknown>>;
		};
		windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];

		deferredGtmEvents.forEach((eventName) => {
			window.addEventListener(eventName, handleDeferredGtmInteraction, {
				once: true,
				passive: true
			});
		});

		const browserWindow = window as BrowserWindow;

		if (browserWindow.requestIdleCallback) {
			deferredGtmIdleCallbackId = browserWindow.requestIdleCallback(() => loadGtmScript(gtmCode), {
				timeout: GTM_DEFER_TIMEOUT_MS
			});
		} else {
			deferredGtmTimeoutId = window.setTimeout(() => loadGtmScript(gtmCode), GTM_DEFER_TIMEOUT_MS);
		}
	}

	function loadTrustedShopsScript(trustedShopsId?: string) {
		if (!trustedShopsId || loadedTrustedShopsId === trustedShopsId) {
			return;
		}

		const trustedShopsSrc = `https://widgets.trustedshops.com/js/${trustedShopsId}.js`;

		if (document.querySelector(`script[src="${trustedShopsSrc}"]`)) {
			loadedTrustedShopsId = trustedShopsId;
			return;
		}

		const appendTrustedShopsScript = () => {
			const trustedShopsScript = document.createElement('script');
			trustedShopsScript.src = trustedShopsSrc;
			trustedShopsScript.async = true;
			trustedShopsScript.dataset.desktopYOffset = '24';
			trustedShopsScript.dataset.mobileYOffset = '64';
			trustedShopsScript.dataset.desktopDisableReviews = 'false';
			trustedShopsScript.dataset.desktopEnableCustom = 'false';
			trustedShopsScript.dataset.desktopPosition = 'right';
			trustedShopsScript.dataset.desktopCustomWidth = '156';
			trustedShopsScript.dataset.desktopEnableFadeout = 'false';
			trustedShopsScript.dataset.disableMobile = 'false';
			trustedShopsScript.dataset.disableTrustbadge = 'false';
			trustedShopsScript.dataset.mobileCustomWidth = '156';
			trustedShopsScript.dataset.mobileDisableReviews = 'false';
			trustedShopsScript.dataset.mobileEnableCustom = 'false';
			trustedShopsScript.dataset.mobilePosition = 'left';
			trustedShopsScript.dataset.mobileEnableTopbar = 'false';
			trustedShopsScript.dataset.mobileEnableFadeout = 'true';
			trustedShopsScript.dataset.colorScheme = 'light';
			trustedShopsScript.charset = 'UTF-8';
			document.head.append(trustedShopsScript);
		};

		const requestIdleCallback = (
			window as Window & {
				requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
			}
		).requestIdleCallback;

		if (requestIdleCallback) {
			requestIdleCallback(appendTrustedShopsScript, { timeout: 3000 });
		} else {
			window.setTimeout(appendTrustedShopsScript, 1500);
		}

		loadedTrustedShopsId = trustedShopsId;
	}

	if (browser) {
		pageType = pageStore.data?.page?.type ?? null;
	}

	onMount(() => {
		mountInitialStores();

		scheduleGtmLoad(data.site.gtmCode);
		scheduleToastLayerMount();
		if (!isHomepageRoute()) {
			loadModalComponent();
		}
		loadTrustedShopsScript(etrustedReviews?.id);

		maybeSetAdvocateId();

		return () => {
			clearDeferredGtmLoad();
			clearDeferredToastMount();
		};
	});

	beforeNavigate(({ to }) => {
		const urlHash = to?.url.hash;

		// Remove the smooth scroll class when navigating to regular URLS
		if (!urlHash && window) {
			window.document.documentElement.classList.remove('smooth-scroll');
		}
	});

	afterNavigate(() => {
		// Make sure the smooth-scroll class is always returned if it was
		// removed for any reason. See code above.
		if (window) {
			window.document.documentElement.classList.add('smooth-scroll');
		}

		// Update feature flags on page load and after navigation
		if (browser && data.flags) {
			maybeUpdateFeatureFlagsFromUrl(data.flags);
		}

		if (!shouldDeferSecondaryStores()) {
			mountSecondaryStores();
		}

		scheduleGtmLoad(data.site.gtmCode);
		scheduleToastLayerMount();

		if (!isHomepageRoute()) {
			loadModalComponent();
		}

		loadTrustedShopsScript(pageStore.data?.page?.layout?.reviews?.etrusted?.id);
	});
</script>

<svelte:head>
	<link rel="icon" type="image/png" href={data.site.favicon} />
	<link rel="preconnect" href={cmsCdnPreconnectHref} crossorigin="anonymous" />
	{#if !shouldDelayGtmLoad()}
		<link rel="preconnect" href="https://www.googletagmanager.com" crossorigin="anonymous" />
	{/if}
	<link rel="preconnect" href="https://use.typekit.net" crossorigin="anonymous" />
	<link rel="preconnect" href="https://p.typekit.net" crossorigin="anonymous" />
	<link rel="dns-prefetch" href="https://widgets.trustedshops.com" />
</svelte:head>

<div class="wrapper {pageType ? `type-${pageType}` : ''}">
	{#if toastComponentPromise}
		{#await toastComponentPromise then ToastComponent}
			{#if ToastComponent}
				<ToastComponent />
			{/if}
		{/await}
	{/if}

	{#if modalComponentPromise}
		{#await modalComponentPromise then ModalComponent}
			{#if ModalComponent}
				<ModalComponent />
			{/if}
		{/await}
	{/if}

	{#if hasPageLayout}
		<Cart />
	{/if}

	{@render children()}
</div>

<style lang="postcss">
	@import '../styles/reset.css';
	@import '../styles/config.css';
	@import '../styles/base.css';
	@import '../styles/parts/slider.css';
	@import '../styles/parts/forms.css';
	@import '../styles/parts/block.css';
	@import '../styles/parts/table.css';
	@import '../styles/parts/columns.css';
</style>
